/**
 * StalenessMonitor - Per-market feed freshness tracker.
 *
 * Groups the merged feed log by ticker, computes the age of the most recent
 * update, and classifies each market as fresh / stale / disconnected.
 * Reads directly from marketStore (no props needed).
 */

import { useMemo } from 'react';
import { useMarketStore } from '@/stores/marketStore';
import type { FeedLogEntry } from '@/stores/marketStore';

// ── Helpers ──────────────────────────────────────────────────────

function shortTicker(ticker: string): string {
  const parts = ticker.split('-');
  return parts[parts.length - 1] || ticker.slice(-6);
}

function fmtAge(ms: number): string {
  if (ms < 1000) return '<1s';
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m`;
  return `${Math.floor(ms / 3_600_000)}h`;
}

type FreshnessLevel = 'fresh' | 'stale' | 'disconnected';

interface TickerFreshness {
  ticker: string;
  source: 'kalshi' | 'polymarket';
  latestTs: number;
  ageMs: number;
  level: FreshnessLevel;
}

function classifyFreshness(ageMs: number): FreshnessLevel {
  if (ageMs < 15_000) return 'fresh';
  if (ageMs < 60_000) return 'stale';
  return 'disconnected';
}

const LEVEL_DOT: Record<FreshnessLevel, string> = {
  fresh:        'bg-green-500',
  stale:        'bg-yellow-500',
  disconnected: 'bg-red-500',
};

const LEVEL_TEXT: Record<FreshnessLevel, string> = {
  fresh:        'text-green-400',
  stale:        'text-yellow-400',
  disconnected: 'text-red-400',
};

// ── Component ────────────────────────────────────────────────────

export function StalenessMonitor() {
  const getFeedLog = useMarketStore((s) => s.getFeedLog);
  const version = useMarketStore((s) => s.version);

  // Force re-derive on version tick
  void version;

  const { items, counts } = useMemo(() => {
    const now = Date.now();
    const entries: FeedLogEntry[] = getFeedLog(); // merged, newest first

    // Group by ticker — find most recent ts per ticker
    const byTicker = new Map<string, { ts: number; source: 'kalshi' | 'polymarket' }>();
    for (const entry of entries) {
      const existing = byTicker.get(entry.ticker);
      if (!existing || entry.ts > existing.ts) {
        byTicker.set(entry.ticker, { ts: entry.ts, source: entry.source });
      }
    }

    // Build freshness items
    const result: TickerFreshness[] = [];
    for (const [ticker, { ts, source }] of byTicker) {
      const ageMs = now - ts;
      result.push({
        ticker,
        source,
        latestTs: ts,
        ageMs,
        level: classifyFreshness(ageMs),
      });
    }

    // Sort by age descending (stalest first)
    result.sort((a, b) => b.ageMs - a.ageMs);

    // Count by level
    let fresh = 0;
    let stale = 0;
    let disconnected = 0;
    for (const item of result) {
      if (item.level === 'fresh') fresh++;
      else if (item.level === 'stale') stale++;
      else disconnected++;
    }

    return { items: result, counts: { fresh, stale, disconnected } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, getFeedLog]);

  return (
    <div className="flex h-full flex-col">
      {/* Summary bar */}
      <div className="border-border/50 flex h-7 shrink-0 items-center gap-3 border-b px-3">
        <SummaryDot color="bg-green-500" label={`${counts.fresh} fresh`} />
        <SummaryDot color="bg-yellow-500" label={`${counts.stale} stale`} />
        <SummaryDot color="bg-red-500" label={`${counts.disconnected} disconnected`} />
      </div>

      {/* Market cards grid */}
      <div className="flex-1 overflow-y-auto p-2">
        {items.length === 0 ? (
          <div className="text-muted-foreground/50 flex h-full items-center justify-center text-[10px]">
            No feed data
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-1 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
            {items.map((item) => (
              <MarketCard key={item.ticker} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── SummaryDot ───────────────────────────────────────────────────

function SummaryDot({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1">
      <div className={`h-1.5 w-1.5 rounded-full ${color}`} />
      <span className="text-muted-foreground text-[10px] tabular-nums">{label}</span>
    </div>
  );
}

// ── MarketCard ───────────────────────────────────────────────────

function MarketCard({ item }: { item: TickerFreshness }) {
  const sourceBadge = item.source === 'kalshi' ? 'K' : 'P';
  const sourceBg = item.source === 'kalshi' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400';

  return (
    <div className="bg-muted/30 flex items-center gap-1.5 rounded px-2 py-1">
      {/* Status dot */}
      <div className={`h-1.5 w-1.5 shrink-0 rounded-full ${LEVEL_DOT[item.level]}`} />

      {/* Ticker */}
      <span className="text-foreground min-w-0 truncate text-[10px] font-medium">
        {shortTicker(item.ticker)}
      </span>

      {/* Source badge */}
      <span className={`shrink-0 rounded px-1 py-px text-[8px] font-bold ${sourceBg}`}>
        {sourceBadge}
      </span>

      {/* Age */}
      <span className={`ml-auto shrink-0 text-[10px] font-mono tabular-nums ${LEVEL_TEXT[item.level]}`}>
        {fmtAge(item.ageMs)}
      </span>
    </div>
  );
}
